/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { SessionState } from '../api/auth';
import type { StreamStatus } from './protocol';
import { readingStreamUrl, type TicketResult } from './ticketClient';

export interface EventSourceLike {
  onopen: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
  addEventListener(type: string, listener: (event: MessageEvent) => void): void;
  close(): void;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

export interface ConnectionManagerDeps {
  requestTicket: (forceRefresh: boolean) => Promise<TicketResult>;
  checkSession: () => Promise<SessionState>;
  createEventSource: EventSourceFactory;
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  random: () => number;
  eventTypes: string[];
  /** Optional retry cap for channels with a tighter recovery target. */
  retryCapMs?: number;
  /** Builds the stream URL from the ticket's base URL and the current keys; defaults to the meter-readings stream. */
  buildUrl?: (streamUrl: string, keys: string[]) => string;
  /** Tells the server a stream is no longer wanted, so it ends it at once rather than at its next heartbeat. */
  closeStream?: (streamId: string) => void;
}

export interface StreamEvent {
  eventType: string;
  data: unknown;
}

export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;
export const STABLE_AFTER_MS = 30_000;
export const REOPEN_DEBOUNCE_MS = 500;
const REAUTH_EVENT = 'reauth';
// The first event of every stream: its id, for closing it on the server.
const STREAM_OPENED_EVENT = 'stream-opened';

function streamIdOf(raw: unknown): string | null {
  try {
    const id = (JSON.parse(typeof raw === 'string' ? raw : '') as { streamId?: unknown }).streamId;
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

type Listener<T> = (value: T) => void;

export class ConnectionManager {
  private status: StreamStatus = 'idle';
  private serials: string[] = [];
  private source: EventSourceLike | null = null;
  private streamId: string | null = null;
  /** The keys the current stream was opened with. */
  private sourceSerials: string | null = null;
  private generation = 0;
  private preOpenFailures = 0;
  private backoffAttempt = 0;
  private forceRefreshNext = false;
  private everOpened = false;
  private recovering = false;
  private halted = false;
  private offline = false;
  private backoffTimer: unknown = null;
  private stableTimer: unknown = null;
  private debounceTimer: unknown = null;
  private malformed = 0;

  private readonly eventListeners: Listener<StreamEvent>[] = [];
  private readonly stateListeners: Listener<StreamStatus>[] = [];
  private readonly reconnectedListeners: Listener<void>[] = [];
  private readonly loginListeners: Listener<void>[] = [];

  constructor(private readonly deps: ConnectionManagerDeps) {}

  onEvent(listener: Listener<StreamEvent>): void {
    this.eventListeners.push(listener);
  }

  onState(listener: Listener<StreamStatus>): void {
    this.stateListeners.push(listener);
  }

  onReconnected(listener: Listener<void>): void {
    this.reconnectedListeners.push(listener);
  }

  onLoginRequired(listener: Listener<void>): void {
    this.loginListeners.push(listener);
  }

  getStatus(): StreamStatus {
    return this.status;
  }

  getMalformedCount(): number {
    return this.malformed;
  }

  setSerials(serials: string[]): void {
    const next = [...new Set(serials)].sort();
    if (next.join(',') === this.serials.join(',')) {
      return;
    }
    this.serials = next;
    this.clear('debounceTimer');
    this.debounceTimer = this.deps.setTimeout(() => {
      this.debounceTimer = null;
      this.applySerials();
    }, REOPEN_DEBOUNCE_MS);
  }

  restart(): void {
    this.halted = false;
    this.preOpenFailures = 0;
    this.backoffAttempt = 0;
    this.forceRefreshNext = false;
    this.everOpened = false;
    this.recovering = false;
    this.applySerials();
  }

  stop(): void {
    this.halted = true;
    this.closeSource(true);
    this.clear('backoffTimer');
    this.clear('debounceTimer');
    this.generation += 1;
    this.setStatus('stopped');
  }

  handleOnline(): void {
    this.offline = false;
    if (this.halted || this.status === 'open' || this.status === 'forbidden' || this.serials.length === 0) {
      return;
    }
    this.clear('backoffTimer');
    void this.connect();
  }

  handleOffline(): void {
    this.offline = true;
    this.closeSource();
    this.clear('backoffTimer');
    this.generation += 1;
    this.recovering = true;
    if (!this.halted && this.status !== 'forbidden') {
      this.setStatus('reconnecting');
    }
  }

  private applySerials(): void {
    if (this.halted || this.status === 'forbidden') {
      return;
    }
    // The keys changed and changed back before the debounce ran: the stream already carries them.
    if (this.source && this.sourceSerials === this.serials.join(',')) {
      return;
    }
    this.closeSource(true);
    this.clear('backoffTimer');
    this.generation += 1;
    this.recovering = false;
    if (this.serials.length === 0) {
      this.setStatus('idle');
      return;
    }
    if (!this.offline) {
      void this.connect();
    }
  }

  private async connect(): Promise<void> {
    const generation = ++this.generation;
    this.setStatus(this.recovering ? 'reconnecting' : 'connecting');
    const forceRefresh = this.forceRefreshNext;
    this.forceRefreshNext = false;
    const ticket = await this.deps.requestTicket(forceRefresh);
    if (generation !== this.generation || this.halted) {
      return;
    }
    switch (ticket.kind) {
      case 'ok':
        this.sourceSerials = this.serials.join(',');
        this.open((this.deps.buildUrl ?? readingStreamUrl)(ticket.streamUrl, this.serials), generation);
        return;
      case 'reauth':
        this.requireLogin();
        return;
      case 'no-access':
        this.closeSource();
        this.setStatus('forbidden');
        return;
      case 'retry-later':
        this.scheduleRetry();
        return;
    }
  }

  private open(url: string, generation: number): void {
    const source = this.deps.createEventSource(url);
    this.source = source;
    let opened = false;
    source.onopen = () => {
      if (generation !== this.generation) return;
      opened = true;
      this.recovering = false;
      const isReconnect = this.everOpened;
      this.everOpened = true;
      this.setStatus('open');
      this.clear('stableTimer');
      this.stableTimer = this.deps.setTimeout(() => {
        this.stableTimer = null;
        this.preOpenFailures = 0;
        this.backoffAttempt = 0;
      }, STABLE_AFTER_MS);
      if (isReconnect) {
        this.reconnectedListeners.forEach((listener) => listener());
      }
    };
    source.onerror = () => {
      if (generation !== this.generation) return;
      this.closeSource();
      void this.handleFailure(opened);
    };
    source.addEventListener(STREAM_OPENED_EVENT, (event) => {
      if (generation !== this.generation) return;
      this.streamId = streamIdOf(event.data);
    });
    source.addEventListener(REAUTH_EVENT, () => {
      if (generation !== this.generation) return;
      this.closeSource();
      this.recovering = true;
      void this.connect();
    });
    this.deps.eventTypes.forEach((eventType) =>
      source.addEventListener(eventType, (event) => this.deliver(eventType, typeof event.data === 'string' ? event.data : undefined)),
    );
  }

  private deliver(eventType: string, raw: string | undefined): void {
    let data: unknown;
    try {
      data = JSON.parse(raw ?? '');
    } catch {
      this.malformed += 1;
      return;
    }
    this.eventListeners.forEach((listener) => listener({ eventType, data }));
  }

  private async handleFailure(afterOpen: boolean): Promise<void> {
    this.preOpenFailures = afterOpen ? 1 : this.preOpenFailures + 1;
    if (this.preOpenFailures === 2) {
      this.forceRefreshNext = true;
    }
    if (this.preOpenFailures >= 3) {
      const generation = this.generation;
      const session = await this.deps.checkSession();
      if (generation !== this.generation || this.halted) return;
      if (session === 'expired') {
        this.requireLogin();
        return;
      }
      this.preOpenFailures = 0;
    }
    this.scheduleRetry();
  }

  private scheduleRetry(): void {
    this.recovering = true;
    this.setStatus('reconnecting');
    const ceiling = Math.min(this.deps.retryCapMs ?? BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.backoffAttempt);
    this.backoffAttempt += 1;
    this.clear('backoffTimer');
    this.backoffTimer = this.deps.setTimeout(() => {
      this.backoffTimer = null;
      if (!this.offline) {
        void this.connect();
      }
    }, this.deps.random() * ceiling);
  }

  private requireLogin(): void {
    this.stop();
    this.loginListeners.forEach((listener) => listener());
  }

  /**
   * Closes the current stream. {@code endOnServer} is for closes the client decides on (keys changed,
   * nothing subscribed, stopped): the server is told so it frees the stream at once. A stream that
   * failed or that the server is ending is just dropped.
   */
  private closeSource(endOnServer = false): void {
    this.clear('stableTimer');
    if (this.source) {
      this.source.onopen = null;
      this.source.onerror = null;
      this.source.close();
      this.source = null;
    }
    this.sourceSerials = null;
    const streamId = this.streamId;
    this.streamId = null;
    if (endOnServer && streamId) {
      this.deps.closeStream?.(streamId);
    }
  }

  private clear(timer: 'backoffTimer' | 'stableTimer' | 'debounceTimer'): void {
    if (this[timer] !== null) {
      this.deps.clearTimeout(this[timer]);
      this[timer] = null;
    }
  }

  private setStatus(status: StreamStatus): void {
    if (status !== this.status) {
      this.status = status;
      this.stateListeners.forEach((listener) => listener(status));
    }
  }
}
